import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ChevronRight, ArrowLeft, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ConversationPost } from '../components/Conversations/ConversationPost';
import { CreateConversationPostForm } from '../components/Conversations/CreateConversationPostForm';
import { NICHES, type ConversationNiche, type ConversationPost as ConversationPostType, type NicheInfo } from '../types/conversations';
import { NICHE_BANNER_URLS } from '../config/nicheBanners';
import { Meta } from '../components/Meta';

// Mapping from niche id to community name keywords for matching (fallback when no custom URL)
const NICHE_COMMUNITY_KEYWORDS: Record<string, string[]> = {
  herbalism: ['herbalism', 'herbs', 'herbal'],
  foraging: ['foraging', 'forage', 'wild food'],
  natural_building: ['natural building', 'building', 'construction'],
  nutrition: ['nutrition', 'nutritional', 'healthy eating'],
  yoga: ['yoga', 'yogic'],
  book_club: ['book club', 'book', 'reading'],
  eco_living: ['eco living', 'eco-living', 'sustainable', 'eco'],
  permaculture: ['permaculture', 'gardening', 'garden'],
  pilates: ['pilates'],
  nervous_system: ['nervous system', 'regulation', 'trauma', 'polyvagal', ' somatic'],
  fasting: ['fasting', 'intermittent fasting', 'water fasting', 'cleansing', 'detox'],
};

export function Conversations() {
  const { t } = useTranslation();
  const [selectedNiche, setSelectedNiche] = useState<ConversationNiche | null>(null);
  const [posts, setPosts] = useState<ConversationPostType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState<{ username?: string; avatar_url?: string }>({});
  const [nicheCounts, setNicheCounts] = useState<Record<string, number>>({});
  const [nicheBanners, setNicheBanners] = useState<Record<string, string>>({});

  useEffect(() => {
    checkAuth();
    fetchNicheCounts();
    fetchCommunityBanners();

    // Auto-select niche from sessionStorage (e.g. when navigating back from a post)
    const savedNiche = sessionStorage.getItem('selectedConversationNiche');
    if (savedNiche) {
      setSelectedNiche(savedNiche as ConversationNiche);
      sessionStorage.removeItem('selectedConversationNiche');
    }
  }, []);

  useEffect(() => {
    if (selectedNiche) {
      fetchPosts(selectedNiche);
    }
  }, [selectedNiche]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsAuthenticated(!!user);
    
    if (user) {
      const { data: profile } = await supabase
        .from('users')
        .select('username, avatar_url')
        .eq('id', user.id)
        .single();
      
      if (profile) {
        setUserProfile(profile);
      }
    }
  };

  const fetchNicheCounts = async () => {
    try {
      // Only fetch the niche column (minimal data) instead of all post data
      const { data, error } = await supabase
        .from('conversation_posts')
        .select('niche', { count: 'exact', head: false });
      
      if (error) throw error;
      
      const counts: Record<string, number> = {};
      data?.forEach(post => {
        counts[post.niche] = (counts[post.niche] || 0) + 1;
      });
      setNicheCounts(counts);
    } catch (error) {
      console.error('Error fetching niche counts:', error);
    }
  };

  const fetchCommunityBanners = async () => {
    try {
      // Start with custom URLs from config (src/config/nicheBanners.ts)
      const banners: Record<string, string> = {};
      for (const [niche, url] of Object.entries(NICHE_BANNER_URLS)) {
        if (url?.trim()) banners[niche] = url.trim();
      }

      // Fill in missing from community matching
      const { data: communities, error } = await supabase
        .from('communities')
        .select('name, banner_url')
        .not('banner_url', 'is', null);

      if (!error && communities) {
        for (const niche of NICHES) {
          if (banners[niche.id]) continue; // already set by custom config

          const keywords = NICHE_COMMUNITY_KEYWORDS[niche.id] || [niche.label.toLowerCase()];
          const matchedCommunity = communities.find(community => {
            const communityName = community.name.toLowerCase();
            return keywords.some(keyword => communityName.includes(keyword));
          });

          if (matchedCommunity?.banner_url) {
            banners[niche.id] = matchedCommunity.banner_url;
          }
        }
      }

      setNicheBanners(banners);
    } catch (error) {
      console.error('Error fetching community banners:', error);
    }
  };

  const fetchPosts = async (niche: ConversationNiche) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('conversation_posts')
        .select(`
          *,
          user:users!conversation_posts_user_id_fkey (
            username,
            avatar_url,
            verified
          ),
          conversation_comments (count)
        `)
        .eq('niche', niche)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = () => {
    if (selectedNiche) {
      fetchPosts(selectedNiche);
      fetchNicheCounts();
    }
  };

  const handlePostDeleted = () => {
    if (selectedNiche) {
      fetchPosts(selectedNiche);
      fetchNicheCounts();
    }
  };

  const selectedNicheInfo = NICHES.find(n => n.id === selectedNiche);

  // Sort niches by post count (most posts first)
  const sortedNiches = [...NICHES].sort((a, b) => {
    const countA = nicheCounts[a.id] || 0;
    const countB = nicheCounts[b.id] || 0;
    return countB - countA;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Meta
        title={`${t('conversations.title')} | Mindful Family`}
        description={t('conversations.subtitle')}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        {/* Banner - shown when niche is selected: community banner or gradient fallback */}
        {selectedNiche ? (
          <div className="w-full">
            <div className="relative w-full">
              <div className="h-36 sm:h-40 lg:h-44 bg-gradient-to-br from-accent-base/30 via-accent-text/20 to-accent-base/10">
                {nicheBanners[selectedNiche] && (
                  <img
                    src={nicheBanners[selectedNiche]}
                    alt={selectedNicheInfo?.label}
                    className={`w-full h-full object-cover ${selectedNiche === 'meditation' ? 'object-[center_30%]' : ''}`}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                <div className="max-w-page mx-auto flex items-center gap-3">
                  <button
                    onClick={() => setSelectedNiche(null)}
                    className="p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors lg:hidden"
                  >
                    <ArrowLeft className="h-5 w-5 text-white" />
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{selectedNicheInfo?.icon}</span>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-white">
                        {selectedNicheInfo?.label}
                      </h1>
                      <p className="text-sm text-white/80 hidden sm:block">
                        {selectedNicheInfo?.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-6">
            <div className="flex items-center gap-3">
              {selectedNiche && (
                <button
                  onClick={() => setSelectedNiche(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <div className="flex items-center gap-3">
                {selectedNiche ? (
                  <span className="text-2xl">{selectedNicheInfo?.icon}</span>
                ) : (
                  <div className="p-2 bg-accent-text/10 rounded-lg">
                    <MessageSquare className="h-6 w-6 text-accent-text" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-content">
                    {selectedNiche ? selectedNicheInfo?.label : t('conversations.title')}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {selectedNiche 
                      ? selectedNicheInfo?.description 
                      : t('conversations.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Niche Categories */}
          <div className={`${selectedNiche ? 'hidden lg:block' : 'w-full lg:w-72'} lg:w-72 flex-shrink-0`}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-24">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-content">{t('conversations.topics')}</h2>
                <p className="text-xs text-gray-500 mt-1">{t('conversations.selectTopic')}</p>
              </div>
              <div className="p-2">
                {sortedNiches.map((niche) => (
                  <button
                    key={niche.id}
                    onClick={() => setSelectedNiche(niche.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all group ${
                      selectedNiche === niche.id 
                        ? 'bg-accent-text/10 text-accent-text' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{niche.icon}</span>
                      <div className="text-left">
                        <div className={`font-medium ${selectedNiche === niche.id ? 'text-accent-text' : 'text-content'}`}>
                          {niche.label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {nicheCounts[niche.id] || 0} posts
                        </div>
                      </div>
                    </div>
                    <ChevronRight className={`h-4 w-4 ${
                      selectedNiche === niche.id 
                        ? 'text-accent-text' 
                        : 'text-gray-400 group-hover:text-gray-600'
                    }`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Posts */}
          <div className={`flex-1 ${!selectedNiche ? 'hidden lg:block' : ''}`}>
            {selectedNiche ? (
              <>
                {/* Back button for desktop */}
                <button
                  onClick={() => setSelectedNiche(null)}
                  className="hidden lg:flex items-center gap-2 text-gray-600 hover:text-content mb-4 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm">Back to all topics</span>
                </button>

                {/* Create Post Form */}
                {isAuthenticated ? (
                  <CreateConversationPostForm
                    niche={selectedNiche}
                    nicheLabel={selectedNicheInfo?.label || ''}
                    onSuccess={handlePostCreated}
                    userProfile={userProfile}
                  />
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 text-center">
                    <p className="text-gray-600 mb-2">Log in to join the conversation</p>
                    <p className="text-sm text-gray-500">Share your thoughts, ask questions, and connect with others</p>
                  </div>
                )}

                {/* Posts List */}
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 animate-pulse">
                        <div className="flex space-x-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                          <div className="flex-1 space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : posts.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                    <div className="text-4xl mb-3">{selectedNicheInfo?.icon}</div>
                    <h3 className="font-semibold text-content mb-2">{t('conversations.noPosts')}</h3>
                    <p className="text-gray-500 text-sm">
                      Be the first to start a conversation about {selectedNicheInfo?.label}!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {posts.map((post) => (
                      <ConversationPost 
                        key={post.id} 
                        post={post}
                        onDelete={handlePostDeleted}
                      />
                    ))}
                  </div>
                )}

                {/* Disclaimer */}
                <div className="mt-6 flex items-start gap-2.5 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                  <Info className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-400">
                    {t('conversations.medicalDisclaimer')}
                  </p>
                </div>
              </>
            ) : (
              /* Welcome message when no niche selected (desktop only) */
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="text-5xl mb-4">💬</div>
                <h3 className="font-semibold text-content mb-2 text-lg">{t('conversations.welcomeTitle')}</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {t('conversations.welcomeText')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
